import { useNavigate } from "react-router-dom";
import {
  Bell,
  ChevronDown,
  LogOut,
  Menu,
  Search,
  Settings as SettingsIcon,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import PillToggle from "@/components/common/PillToggle";
import HitlChip from "@/components/hitl/HitlChip";
import { useStore } from "@/lib/store";
import { useAuth } from "@/lib/authContext";

const initialsFor = (name) =>
  (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0].toUpperCase())
    .join("") || "?";

export default function Topbar({ onMenuClick }) {
  const mode = useStore((s) => s.mode);
  const setMode = useStore((s) => s.setMode);
  const profile = useStore((s) => s.profile);
  const openCmdk = useStore((s) => s.openCmdk);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const initials = initialsFor(user?.name);

  const handleLogout = async () => {
    await logout();
  };

  return (
    <header
      data-testid="topbar"
      className="flex flex-shrink-0 items-center gap-3 border-b border-black/[0.04] bg-[#F7F7F5]/85 px-4 py-3 backdrop-blur md:gap-6 md:px-8 md:py-4"
    >
      {/* Mobile-only hamburger */}
      <button
        type="button"
        data-testid="topbar-menu-btn"
        onClick={onMenuClick}
        className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border border-black/[0.04] bg-white text-zinc-700 transition-colors hover:bg-zinc-50 md:hidden"
        aria-label="Open menu"
      >
        <Menu className="h-4 w-4" strokeWidth={2.4} />
      </button>

      <div className="flex min-w-0 flex-shrink-0 flex-col leading-tight md:min-w-[220px]">
        <div className="flex items-center gap-2">
          <div
            data-testid="company-name"
            className="truncate text-[16px] font-extrabold tracking-tight text-zinc-900 sm:text-[20px] md:text-[24px]"
          >
            {profile.name}
          </div>
          <span
            data-testid="company-stage"
            className="hidden flex-shrink-0 rounded-full border border-black/[0.06] bg-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-700 sm:inline-block"
          >
            {profile.stage}
          </span>
        </div>
        <div className="mt-0.5 hidden text-[12px] text-zinc-500 xl:block">
          {profile.tagline}
        </div>
      </div>

      {/* Desktop search button */}
      <div className="hidden min-w-0 flex-1 md:block">
        <button
          type="button"
          data-testid="topbar-search"
          onClick={openCmdk}
          className="mx-auto flex h-11 w-full max-w-[640px] items-center gap-3 rounded-full border border-black/[0.04] bg-white px-4 text-left shadow-[0_1px_0_rgba(0,0,0,0.02),0_8px_24px_-12px_rgba(15,15,25,0.08)] transition-colors hover:bg-zinc-50"
        >
          <Search className="h-4 w-4 flex-shrink-0 text-zinc-400" strokeWidth={2.2} />
          <span
            data-testid="topbar-search-placeholder"
            className="flex-1 truncate text-[14px] text-zinc-400"
          >
            Ask the boardroom anything...
          </span>
          <kbd className="rounded-md border border-black/[0.06] bg-zinc-50 px-1.5 py-0.5 font-mono text-[10px] text-zinc-500">
            ⌘K
          </kbd>
        </button>
      </div>

      {/* Spacer pushes right cluster to the edge on mobile */}
      <div className="flex-1 md:hidden" />

      <div className="flex flex-shrink-0 items-center gap-2 md:gap-4">
        {/* Mobile-only icon search */}
        <button
          type="button"
          data-testid="topbar-search-mobile"
          onClick={openCmdk}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-black/[0.04] bg-white text-zinc-700 transition-colors hover:bg-zinc-50 md:hidden"
          aria-label="Search"
        >
          <Search className="h-4 w-4" strokeWidth={2.2} />
        </button>

        {/* Mode toggle — desktop only */}
        <div className="hidden md:block">
          <PillToggle
            testId="mode-toggle"
            value={mode}
            onChange={setMode}
            options={[
              { value: "bootstrap", label: "Bootstrap" },
              { value: "funding", label: "Funding" },
            ]}
          />
        </div>

        <HitlChip />

        <button
          type="button"
          data-testid="topbar-bell"
          className="relative flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border border-black/[0.04] bg-white text-zinc-700 transition-colors hover:bg-zinc-50 md:h-9 md:w-9"
          aria-label="Notifications"
        >
          <Bell className="h-4 w-4" strokeWidth={2.2} />
          <span className="absolute right-2 top-2 h-1.5 w-1.5 rounded-full bg-rose-500" />
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              data-testid="topbar-user"
              className="flex items-center gap-2 rounded-full border border-black/[0.04] bg-white py-1 pl-1 pr-1 transition-colors hover:bg-zinc-50 md:pr-3"
            >
              {user?.picture ? (
                <img
                  src={user.picture}
                  alt={user?.name || ""}
                  className="h-8 w-8 rounded-full object-cover md:h-7 md:w-7"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-zinc-900 to-zinc-700 text-[11px] font-bold text-white md:h-7 md:w-7">
                  {initials}
                </span>
              )}
              <span
                data-testid="topbar-user-name"
                className="hidden text-[13px] font-semibold text-zinc-800 lg:inline"
              >
                {user?.name || "Account"}
              </span>
              <ChevronDown
                className="hidden h-3.5 w-3.5 text-zinc-500 md:block"
                strokeWidth={2.4}
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56" data-testid="topbar-user-menu">
            <DropdownMenuLabel className="truncate text-[12px] text-zinc-500">
              {user?.email || "Signed in"}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              data-testid="topbar-user-settings"
              onClick={() => navigate("/settings")}
            >
              <SettingsIcon className="mr-2 h-4 w-4" strokeWidth={2.2} />
              Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              data-testid="topbar-user-logout"
              onClick={handleLogout}
              className="text-rose-600 focus:bg-rose-50 focus:text-rose-700"
            >
              <LogOut className="mr-2 h-4 w-4" strokeWidth={2.2} />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
